import { faExpand, faSave } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { FC, useCallback, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { Button } from 'src/components/Button';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { PaymentMethodSplitFormRef } from 'src/components/PaymentMethodSplitForm';
import { Stack } from 'src/components/Stack';
import { StickyCard, StickyCardSkeleton } from 'src/components/StickyCard';
import { Tab, Tabs } from 'src/components/Tabs';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { FeatureFlag } from 'src/models/platform/_types';
import { startUpdateCC } from 'src/models/user/user';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { DebouncedFormik } from 'src/shared/Forms/DebouncedFormik';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { PaymentPlanSelect } from 'src/shared/Forms/Selects/PaymentPlanSelect';
import {
  hasFeatureFlag,
  usePermissionCheck,
} from 'src/shared/usePermissionCheck';
import { decamelize } from 'src/utils/decamelize';
import { getStartOfDayUTCTime } from 'src/utils/utils';
import * as Yup from 'yup';

import { ContentInfoForm } from './Components/ContentInfoForm';
import { PersonalInfoForm } from './Components/PersonalInfoForm';
import { useNewClientPage } from './useNewClientPage';

interface NewClientPageProps {
  fromLead?: boolean;
}

const NewClientPage: FC<NewClientPageProps> = ({ fromLead }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'client.newClientPage',
  });
  const { t: tPersonalInfo } = useTranslation('translation', {
    keyPrefix: 'client.personalInfo',
  });
  const { t: tGlobal } = useTranslation('translation');

  const { session, user } = useSession();
  const pageTitle = `${t('addNewAccount')} ${fromLead ? t('fromLead') : ''}`;

  const {
    canAccessFrontDeskAsStaff,
    canAccessFrontDeskAsAdmin,
    canManageStripe,
    canConnectStripe,
  } = usePermissionCheck();

  const paymentFormRefs = useRef<PaymentMethodSplitFormRef[] | null[]>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const emails = searchParams.get('emails');
  const [addMany, setAddMany] = useState(!!emails);
  const [showIndividualTraining, setShowIndividualTraining] = useState(false);
  const [showContentInfo, setShowContentInfo] = useState(false);
  const [hasAnyCards, setHasAnyCards] = useState<boolean[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { lifecycleStages, createClientMutation, getInitialProfileFields } =
    useNewClientPage();

  const { data: clientSecret, isLoading } = useQuery({
    queryKey: ['clientSecret'],
    queryFn: () =>
      startUpdateCC({
        forClient: true,
      }),
    select: (res) => res.clientSecret,
    staleTime: 0,
    gcTime: 0,
  });

  const generateValidationMsg = (
    name:
      | 'email'
      | 'firstName'
      | 'lastName'
      | 'streetAddress'
      | 'city'
      | 'zipCode'
  ) => {
    return tGlobal('common.form.required', {
      field: tPersonalInfo(name),
    });
  };

  const isRequired = (id: string) => {
    return !!session?.newClientRequiredFields?.find((f) => f.id === id);
  };

  const fieldSchema = [
    'firstName',
    'lastName',
    'streetAddress',
    'city',
    'zipCode',
  ].reduce(
    (acc, field) => ({
      ...acc,
      [field]: isRequired(decamelize(field))
        ? Yup.string().required(generateValidationMsg(field as any))
        : Yup.string(),
    }),
    {}
  );

  const validateSchema = Yup.object().shape({
    invites: Yup.array().of(
      Yup.object().shape(
        addMany
          ? {}
          : {
              email: Yup.string()
                .email(tGlobal('login.invalid-email'))
                .required(generateValidationMsg('email')),
              ...fieldSchema,
              state: isRequired('state')
                ? Yup.string().required(
                    tGlobal('common.form.required', {
                      field: t('state'),
                    })
                  )
                : Yup.string(),
              profileFields: Yup.array().of(
                Yup.object().shape({
                  name: Yup.string(),
                  id: Yup.string(),
                  value: Yup.string()
                    .nullable()
                    .test('required-based-on-name', '', function (val) {
                      const { name, id } = this.parent;
                      if (!val && isRequired(id)) {
                        return this.createError({
                          path: this.path,
                          message: tGlobal('common.form.required', {
                            field: name,
                          }),
                        });
                      }
                      return true;
                    }),
                })
              ),
            }
      )
    ),
    emails: addMany
      ? Yup.string().required(t('pleaseEnterOneOrMoreEmailAddresses'))
      : Yup.string(),
  });

  const defaultAccount = {
    email: searchParams.get('email') || '',
    firstName: searchParams.get('first_name') || '',
    lastName: searchParams.get('last_name') || '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    lifecycleStageId: '',
    secret: clientSecret,
    swipeCard: false,
    paymentMethodId: null,
    profileFields: getInitialProfileFields(searchParams.get('phone_number')),
  };

  const formikProps = {
    initialValues: {
      invites: [defaultAccount],
      emails: emails || '',
      doNotSendEmail: false,
      // training
      planId: '',
      // content info
      sequenceId: null,
      assessmentId: null,
      assessmentReminder: null,
      requiredSequence: null,
      requiredAssessment: null,
      groupToJoin: null,
      notes: null,
      trainerId: null,
      timeZone: null,
      tags: [],
      resourceMessage: null,
      resourceGroup: [],
      welcome: null,
      locationId: null,
      weekPlans: [],
      // packages
      packageData: [],
      curPackagePage: null,
      // assessment sequence
      assessmentValue: null,
      requiredAssessmentValue: null,
    },
    validationSchema: validateSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const data = {
        groupToJoin: values.groupToJoin,
        notes: values.notes,
        welcomeMessage: values.welcome,
        subTrainerId: values.trainerId,
        planId: values.planId,
        tags: values.tags,
        assessmentId: values.assessmentId,
        sequenceId: values.sequenceId,
        requiredAssessment: values.requiredAssessment,
        requiredSequence: values.requiredSequence,
        assessmentReminder: values.assessmentReminder,
        resourceIds: values.resourceGroup.map((r) => r.id).join(','),
        resourceMessage: values.resourceMessage,
        timeZone: values.timeZone,
        locationId: values.locationId,
        assignPackages: values.packageData,
        doNotSendEmail: values.doNotSendEmail,
        importWeekPlans: values.weekPlans.length > 0 ? values.weekPlans : null,
      };
      try {
        if (addMany) {
          const emails = values.emails
            .replace(/,,/g, ',')
            .replace(/\s|^,*|,*$/g, '')
            .split(',')
            .filter((v) => !!v);

          for (let n = 0; n < emails.length; n += 1) {
            const email = emails[n].replace(/(<|>| )/g, '');
            emails[n] = email.toLowerCase();
          }
          await createClientMutation.mutateAsync({
            data: {
              ...data,
              emails: emails,
            },
            bulk: true,
          });
        } else {
          const promises = paymentFormRefs.current.map((ref) => ref?.submit());
          const res = await Promise.all(promises);
          if (res.some((r) => r?.error)) return;
          const invites = values.invites.map((v, i) => ({
            ...v,
            cardToken: res?.[i]?.setupIntent?.id || null,
            profileFields: v.profileFields.map((f) => ({
              ...f,
              value:
                f.id === 'birthdate' && f.value
                  ? getStartOfDayUTCTime(f.value)
                  : f.value || null,
            })),
          }));
          await createClientMutation.mutateAsync({
            data,
            invites: invites,
            bulk: false,
          });
        }
      } catch (error) {
        // ignore
      } finally {
        setLoading(false);
      }
    },
  };

  const shouldShowWelcomeEmailCheckbox = useCallback(
    (values) => {
      if (addMany) {
        // For bulk addition, check if the default stage is a client stage
        const defaultStage = lifecycleStages.find((l) => l.default === true);
        return defaultStage?.client === true;
      }

      // For individual additions, check if any invite has a client lifecycle stage
      const clientStageIds = lifecycleStages
        .filter((stage) => stage.client === true)
        .map((stage) => stage.id);

      return values.invites.some((invite) =>
        clientStageIds.includes(parseInt(invite.lifecycleStageId))
      );
    },
    [addMany, lifecycleStages]
  );

  if (isLoading) {
    return <StickyCardSkeleton />;
  }

  return (
    <>
      <Meta title={pageTitle} brand={session?.brand} />
      <AppFrameConfig
        id="newClient"
        containerMaxWidth={'xl'}
        title={pageTitle}
        backButtonTitle={fromLead ? t('allCompletedLead') : t('allAccounts')}
        backButtonLinkTo={fromLead ? externalLinks.leads() : '/clients'}
      />
      <Tabs
        value={addMany ? 1 : 0}
        onChange={(event, newValue) => {
          setAddMany(newValue === 1);
          if (newValue === 1) {
            navigate('/clients/new?emails=', { replace: true });
          } else {
            navigate('/clients/new', { replace: true });
          }
        }}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label={t('addIndividualClients')} />
        <Tab label={t('addMultipleClients')} />
      </Tabs>
      <DebouncedFormik {...formikProps}>
        {({ values, setFieldValue, submitForm }) => {
          return (
            <Stack spacing={3} pb={3}>
              <PersonalInfoForm
                fromLead={fromLead}
                isRequired={isRequired}
                addMany={addMany}
                setHasAnyCards={setHasAnyCards}
                setPaymentFormRefs={(index, node) => {
                  paymentFormRefs.current[index] = node;
                }}
              />
              {(!hasFeatureFlag(session, FeatureFlag.HideOnlineTraining) &&
                !showIndividualTraining &&
                !(canAccessFrontDeskAsStaff || canAccessFrontDeskAsAdmin) &&
                canManageStripe) ||
              !showContentInfo ? (
                <Stack sx={{ flexDirection: 'row', gap: 2, py: 0 }}>
                  {!hasFeatureFlag(session, FeatureFlag.HideOnlineTraining) &&
                    !showIndividualTraining &&
                    !(canAccessFrontDeskAsStaff || canAccessFrontDeskAsAdmin) &&
                    canManageStripe && (
                      <Button
                        variant="outlined"
                        startIcon={<Icon icon={faExpand} />}
                        onClick={() => setShowIndividualTraining(true)}
                      >
                        {t('onlineTrainingSubscription')}
                      </Button>
                    )}
                  {!showContentInfo && (
                    <Button
                      variant="outlined"
                      startIcon={<Icon icon={faExpand} />}
                      onClick={() => setShowContentInfo(true)}
                    >
                      {t('assignOrUpdateProfile')}
                    </Button>
                  )}
                </Stack>
              ) : null}
              {showIndividualTraining && (
                <StickyCard
                  title={
                    <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('onlineTrainingSubscription')}{' '}
                      <Link
                        component="button"
                        sx={{ ml: 1 }}
                        onClick={() => {
                          setFieldValue('planId', null);
                          setShowIndividualTraining(false);
                        }}
                      >
                        ({t('remove')})
                      </Link>
                    </Typography>
                  }
                  subtitle={t('onlineTrainingSubscriptionDesc')}
                >
                  {hasAnyCards.includes(true) ? (
                    <Typography>
                      {t('youCannotAddCreditCard', {
                        clients: tGlobal('trainer.client.other').toLowerCase(),
                      })}
                    </Typography>
                  ) : user.accountConfigured ? (
                    <>
                      <PaymentPlanSelect
                        label={t('pleaseChoosePaymentPlan')}
                        userId={undefined as any}
                        value={values.planId}
                        onChange={(p) => {
                          if (Array.isArray(p)) {
                            setFieldValue('planId', p[0]?.id ?? '');
                          } else {
                            setFieldValue('planId', p?.id ?? '');
                          }
                        }}
                      />
                      {canConnectStripe && (
                        <Link href={externalLinks.stripeSubscriptions()}>
                          {t('managePaymentPlans')}
                        </Link>
                      )}
                    </>
                  ) : (
                    <Typography>
                      <Trans
                        t={t}
                        i18nKey="pleaseConnectStripe"
                        components={{
                          1: <Link component={RouterLink} to={'/me/apps'} />,
                        }}
                      />
                    </Typography>
                  )}
                </StickyCard>
              )}
              {showContentInfo && (
                <ContentInfoForm setShowContentInfo={setShowContentInfo} />
              )}
              {shouldShowWelcomeEmailCheckbox(values) && (
                <FormCheckbox
                  name="doNotSendEmail"
                  label={
                    (
                      <HtmlTypography>
                        {t('dontSendWelcomeEmail')}
                      </HtmlTypography>
                    ) as any
                  }
                />
              )}
              <Button
                variant="contained"
                onClick={submitForm}
                startIcon={<Icon icon={faSave} />}
                sx={{ width: 'fit-content' }}
                loading={loading}
                disabled={loading}
              >
                {t('addAccounts')}
              </Button>
            </Stack>
          );
        }}
      </DebouncedFormik>
    </>
  );
};

export default NewClientPage;
